import { Routes } from "@angular/router";

import { AdminLayoutComponent } from "./layouts/admin/admin-layout.component";
import { AuthLayoutComponent } from "./layouts/auth/auth-layout.component";
import { AuthRoleGuard } from "./modules/auth/guards/auth-role.guard";
import { AuthTokenGuard } from "./modules/auth/guards/auth-token.guard";
import { ForcePasswordChangeGuard } from "./modules/auth/guards/force-password-change.guard";
import { NoProfileGuard } from "./modules/auth/guards/no-profile.guard";
import { AppProperties } from "./app.properties";

export const APP_NAV_ROLES = {
  miPortal: {
    any: [AppProperties.VACATION_CREATE, AppProperties.PAYROLL_STATEMENT_READ, AppProperties.CONTRIBUTOR_VIEW, AppProperties.CONTRIBUTOR_VIEW_SUBCONTRATO],
    misVacaciones: [AppProperties.VACATION_CREATE],
    misLiquidaciones: [AppProperties.PAYROLL_STATEMENT_READ],
    misLiquidacionesSubcontrato: [AppProperties.CONTRIBUTOR_VIEW_SUBCONTRATO],
  },
  finanzas: {
    any: [
      AppProperties.CHARGE_ACCOUNT_READ,
      AppProperties.COST_CENTER_READ,
      AppProperties.ADM_AND_FIN_LIQUIDATION,
      AppProperties.FACTURACION_READ,
    ],
    centroCosto: [AppProperties.COST_CENTER_READ],
    cuentasCargo: [AppProperties.CHARGE_ACCOUNT_READ],
    liquidacionesSueldo: [AppProperties.ADM_AND_FIN_LIQUIDATION],
    facturacion: {
      any: [AppProperties.FACTURACION_READ],
      registro: [AppProperties.FACTURACION_WRITE],
    },
  },
  personas: {
    any: [AppProperties.PEOPLE_READ, AppProperties.CANDIDATE_READ],
    listPersonas: [AppProperties.PEOPLE_READ],
    reclutamiento: [AppProperties.PEOPLE_READ, AppProperties.CANDIDATE_READ],
    candidatos: [AppProperties.CANDIDATE_READ],
  },
  proyecto: {
    any: [
      AppProperties.PROJECT_READ,
      AppProperties.PROJECT_LEAD_APPROVER,
      AppProperties.HR_APPROVER,
      AppProperties.SUBCONTRACT_READ,
      AppProperties.SUBCONTRACT_APPROVE,
      AppProperties.COUNTRY_WRITE,
    ],
    mantencion: [AppProperties.PROJECT_READ],
    misAprobaciones: [
      AppProperties.PROJECT_LEAD_APPROVER,
      AppProperties.SUBCONTRACT_APPROVE_LEADER,
      AppProperties.SUBCONTRACT_APPROVE_FINANZAS,
      AppProperties.HR_APPROVER
    ],
    liquidacionesSubcontrato: [
      AppProperties.SUBCONTRACT_READ,
      AppProperties.SUBCONTRACT_APPROVE_LEADER,
      AppProperties.SUBCONTRACT_APPROVE_FINANZAS,
    ],
    periodoActivo: [AppProperties.COUNTRY_WRITE],
  },
  timeReport: {
    any: [
      AppProperties.PROJECT_READ,
      AppProperties.CHARGE_ACCOUNT_READ,
      AppProperties.COST_CENTER_READ,
      AppProperties.TIMESHEET_CREATE,
    ],
    migrator: [AppProperties.MIGRATOR],
  },
  reportes: {
    any: [
      AppProperties.PEOPLE_READ,
      AppProperties.PROJECT_LEAD_APPROVER,
      AppProperties.HR_APPROVER,
    ],
    cargableTr: [AppProperties.PEOPLE_READ],
    vacaciones: [AppProperties.PROJECT_LEAD_APPROVER, AppProperties.HR_APPROVER],
  },
  admin: {
    any: [
      AppProperties.USER_CREATE,
      AppProperties.PROFILE_CREATE,
      AppProperties.ROLE_CREATE,
      AppProperties.API_MANAGE,
      AppProperties.COMPANY_CREATE,
    ],
    usuarios: [AppProperties.USER_CREATE],
    perfiles: [AppProperties.PROFILE_CREATE],
    roles: [AppProperties.ROLE_CREATE],
    timesheet: [AppProperties.TIMESHEET_MANAGER],
    apiKeys: [AppProperties.API_MANAGE],
    compania: [AppProperties.COMPANY_CREATE],
  },
  configuracion: {
    any: [AppProperties.COUNTRY_READ],
    pais: [AppProperties.COUNTRY_READ],
    calendario: [AppProperties.COUNTRY_READ],
    feriados: [AppProperties.COUNTRY_READ],
    parametrosGenerales: [AppProperties.PARAM_READ],
  },
} as const;

export type AppNavRoles = typeof APP_NAV_ROLES;

export const AppRoutes: Routes = [
  {
    path: "",
    redirectTo: "auth/oauth-redirect",
    pathMatch: "full",
  },
  {
    path: "",
    component: AdminLayoutComponent,
    canActivate: [ForcePasswordChangeGuard, NoProfileGuard],
    children: [
      {
        path: "inicio",
        canActivate: [AuthTokenGuard],
        loadChildren: () =>
          import("./modules/inicio/inicio.module").then((x) => x.InicioModule),
      },
      {
        path: "personas",
        canActivate: [AuthRoleGuard],
        data: {
          roles: [...APP_NAV_ROLES.personas.any],
        },
        loadChildren: () =>
          import("./modules/personas/personas.module").then(
            (x) => x.PersonasModule
          ),
      },
      {
        path: "proyecto",
        canActivate: [AuthRoleGuard],
        data: {
          roles: [...APP_NAV_ROLES.proyecto.any],
        },
        loadChildren: () =>
          import("./modules/proyecto/proyecto.module").then(
            (x) => x.ProyectoModule
          ),
      },
      {
        path: "finanzas",
        canActivate: [AuthRoleGuard],
        data: {
          roles: [...APP_NAV_ROLES.finanzas.any],
        },
        loadChildren: () =>
          import("./modules/finanzas/finanzas.module").then(
            (x) => x.FinanzasModule
          ),
      },
      {
        path: "time-report",
        canActivate: [AuthRoleGuard],
        data: {
          roles: [...APP_NAV_ROLES.timeReport.any],
        },
        loadChildren: () =>
          import("./modules/time-report/time-report.module").then(
            (x) => x.TimeReportModule
          ),
      },
      {
        path: "reportes",
        canActivate: [AuthRoleGuard],
        data: {
          roles: [...APP_NAV_ROLES.reportes.any],
        },
        loadChildren: () =>
          import("./modules/reportes/reportes.module").then(
            (x) => x.ReportesModule
          ),
      },
      {
        path: "mi-portal",
        canActivate: [AuthRoleGuard],
        data: {
          roles: [...APP_NAV_ROLES.miPortal.any],
        },
        loadChildren: () =>
          import("./modules/mi-portal/mi-portal.module").then(
            (x) => x.MiPortalModule
          ),
      },
      {
        path: "users",
        loadChildren: () =>
          import("./modules/users/users.module").then(
            (x) => x.UsersModule
          ),
      },
      {
        path: "admin",
        canActivate: [AuthRoleGuard],
        data: {
          roles: [...APP_NAV_ROLES.admin.any],
        },
        loadChildren: () =>
          import("./modules/admin/admin.module").then(
            (x) => x.AdminModule
          ),
      },
      {
        path: "configuracion",
        canActivate: [AuthRoleGuard],
        data: {
          roles: [...APP_NAV_ROLES.configuracion.any],
        },
        loadChildren: () =>
          import("./modules/configuracion/configuracion.module").then(
            (x) => x.ConfiguracionModule
          ),
      },
    ],
  },
  {
    path: "",
    component: AuthLayoutComponent,
    children: [
      {
        path: "auth",
        loadChildren: () =>
          import("./modules/auth/auth.module").then((x) => x.AuthModule),
      },
    ],
  },
];
